import type { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { GableRoofExtrusion } from "building-types/roofs/gableRoofExtrusionTypes";
import type { RoofWallFaces } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { RoofSide } from "building-types/settings/roofTypes";
import { RoofStructureType } from "building-types/settings/settingsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { getMaxDepthErrorHandle } from "doc/building/buildingGenerationIssuesTools";
import { DEFAULT_VAULTED_ROOF_THICKNESS, MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import type { Face } from "geom/brep";
import { faceOrNullReverse, faceNew, faceOrNullMoveAlongNormal, faceOrNullCopy } from "geom/brep";
import { addConvexPlanesToBSPLeaf, buildPolygonsFromFaces, buildBSPNode, buildEdgePolygon } from "geom/bsp";
import { clipFaces, clippingNodeNew, clipFace } from "geom/bspClipping";
import type { PolygonBSP } from "geom/bspTypes";
import { BSP_EPSILON } from "geom/bspTypes";
import { offsetPolyLine2 } from "geom/offset";
import { planeMoveAlongNormal, planeCopy } from "geom/plane";
import { planeBSPFromPlane } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2Length,
  polyLine2ExtendStart,
  polyLine2ExtendEnd,
  polyLine2ByTwoPoints,
  polyLine2EdgeCount,
  polyLine2EdgeStart,
  polyLine2EdgeEnd,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { getRoofSideWallFramingType } from "../roofs/roofEdges";
import { roofWallFacesFromFrontFace } from "../roofs/roofWallFaces";

interface MakeRoofBottomFacesOptions {
  id: string;
  leftRail: PolyLine2;
  rightRail: PolyLine2;
  basePlane: Plane;
  leftRoofOverhang: number;
  rightRoofOverhang: number;
  startRoofOverhang: number;
  endRoofOverhang: number;
  wallSettings: WallSettings;
  bottomPerimeter: PolyLine2 | null;
  hasStartExtension: boolean;
  hasEndExtension: boolean;
  extrusion: GableRoofExtrusion;
  startFrontFace: Face | null;
  endFrontFace: Face | null;
  overhangDatum: WallLateralDatumAnchor;
  edges: RoofEdge[];
  issues: BuildingGenerationIssue[];
  structureType?: RoofStructureType;
  vaultedRoofThickness?: number;
  vaultedRoofRightThickness?: number;
}

interface MakeRoofBottomFacesResult {
  bottomFaces: Face[];
  vaultedRoofCapFaces: RoofWallFaces[];
}

export function makeRoofBottomFaces({
  id,
  leftRail,
  rightRail,
  basePlane,
  leftRoofOverhang,
  rightRoofOverhang,
  startRoofOverhang,
  endRoofOverhang,
  wallSettings,
  bottomPerimeter,
  hasStartExtension,
  hasEndExtension,
  extrusion,
  startFrontFace,
  endFrontFace,
  overhangDatum,
  edges,
  issues,
  structureType = RoofStructureType.FLAT,
  vaultedRoofThickness = DEFAULT_VAULTED_ROOF_THICKNESS,
  vaultedRoofRightThickness = vaultedRoofThickness,
}: MakeRoofBottomFacesOptions): MakeRoofBottomFacesResult {
  let bottomFaces: Face[] = [];
  const vaultedRoofCapFaces: RoofWallFaces[] = [];

  const exteriorWallOffsetFromOverhangDatum = getExteriorWallOffset(wallSettings, overhangDatum);

  const leftSideOffset = leftRoofOverhang + BSP_EPSILON * 2.0;
  const rightSideOffset = rightRoofOverhang + BSP_EPSILON * 2.0;
  const startOffset = startRoofOverhang + BSP_EPSILON * 2.0;
  const endOffset = endRoofOverhang + BSP_EPSILON * 2.0;

  let adjustedLeftRail = offsetPolyLine2(leftRail, leftSideOffset);
  let adjustedRightRail = offsetPolyLine2(rightRail, -rightSideOffset);

  if (!hasStartExtension) {
    if (polyLine2Length(adjustedLeftRail) < startOffset || polyLine2Length(adjustedRightRail) < startOffset) {
      return { bottomFaces, vaultedRoofCapFaces };
    }

    adjustedLeftRail = polyLine2ExtendStart(adjustedLeftRail, -startOffset);
    adjustedRightRail = polyLine2ExtendStart(adjustedRightRail, -startOffset);
  } else if (structureType === RoofStructureType.VAULTED) {
    adjustedLeftRail = polyLine2ExtendStart(adjustedLeftRail, startOffset);
    adjustedRightRail = polyLine2ExtendStart(adjustedRightRail, startOffset);
  }

  if (!hasEndExtension) {
    if (polyLine2Length(adjustedLeftRail) < endOffset || polyLine2Length(adjustedRightRail) < endOffset) {
      return { bottomFaces, vaultedRoofCapFaces };
    }

    adjustedLeftRail = polyLine2ExtendEnd(adjustedLeftRail, -endOffset);
    adjustedRightRail = polyLine2ExtendEnd(adjustedRightRail, -endOffset);
  } else if (structureType === RoofStructureType.VAULTED) {
    adjustedLeftRail = polyLine2ExtendEnd(adjustedLeftRail, endOffset);
    adjustedRightRail = polyLine2ExtendEnd(adjustedRightRail, endOffset);
  }

  const bottomLoop = polyLine2New([
    adjustedLeftRail[0],
    adjustedLeftRail[1],
    adjustedLeftRail[2],
    adjustedLeftRail[3],
    adjustedRightRail[2],
    adjustedRightRail[3],
    adjustedRightRail[0],
    adjustedRightRail[1],
    adjustedLeftRail[0],
    adjustedLeftRail[1],
  ]);

  const bottomPlane = planeMoveAlongNormal(planeCopy(basePlane), 0.1);

  const mainBottomFace = faceOrNullReverse(faceNew([bottomLoop], bottomPlane));

  switch (structureType) {
    case RoofStructureType.FLAT: {
      if (mainBottomFace) {
        bottomFaces.push(mainBottomFace);
      }

      break;
    }
    case RoofStructureType.VAULTED: {
      // vaulted faces
      const leftVaultedFaces: Face[] = [];
      const rightVaultedFaces: Face[] = [];
      const frontVaultedFaces: Face[] = [];
      const backVaultedFaces: Face[] = [];

      const addVaultedFaces = (vaultedFaces: Face[], faces: Face[], thickness: number) => {
        vaultedFaces.push(
          ...(faces
            .map((x) => {
              return faceOrNullMoveAlongNormal(faceOrNullReverse(faceOrNullCopy(x)), thickness);
            })
            .filter((x) => !!x) as Face[]),
        );
      };

      const { leftFaces, rightFaces, startHipFaces, endHipFaces } = extrusion;

      addVaultedFaces(leftVaultedFaces, leftFaces, vaultedRoofThickness);
      addVaultedFaces(rightVaultedFaces, rightFaces, vaultedRoofRightThickness);
      addVaultedFaces(frontVaultedFaces, startHipFaces, vaultedRoofThickness);
      addVaultedFaces(backVaultedFaces, endHipFaces, vaultedRoofRightThickness);

      const clipVaultedBSP = addConvexPlanesToBSPLeaf([planeBSPFromPlane(basePlane)]);

      clipFaces({
        faces: [...leftVaultedFaces, ...rightVaultedFaces, ...frontVaultedFaces, ...backVaultedFaces],
        options: {
          clippingNodes: [clippingNodeNew(clipVaultedBSP)],
          epsilon: MULTIGABLE_BSP_EPSILON,
        },
        clippedFaces: bottomFaces,
      });

      // flat faces (parallel to the ground)
      const vaultedFaceGroups = [leftVaultedFaces, rightVaultedFaces, frontVaultedFaces, backVaultedFaces];
      for (const vaultedFaceGroup of vaultedFaceGroups) {
        if (vaultedFaceGroup.length === 0) {
          continue;
        }

        const vaultedPolygons: PolygonBSP[] = buildPolygonsFromFaces({ faces: vaultedFaceGroup });
        const clipFlatBSP = buildBSPNode({
          polygons: vaultedPolygons,
          maxDepthErrorHandle: getMaxDepthErrorHandle(issues, { type: "ROOF", id }),
        });

        if (mainBottomFace) {
          clipFace({
            face: mainBottomFace,
            options: {
              clippingNodes: [clippingNodeNew(clipFlatBSP, true)],
              epsilon: MULTIGABLE_BSP_EPSILON,
            },
            clippedFaces: bottomFaces,
          });
        }
      }

      // cap walls
      if (hasEndExtension && startFrontFace) {
        const extraFrontFace = faceOrNullCopy(startFrontFace);
        faceOrNullMoveAlongNormal(extraFrontFace, -polyLine2Length(leftRail));

        if (extraFrontFace) {
          vaultedRoofCapFaces.push(
            roofWallFacesFromFrontFace(
              getRoofSideWallFramingType(edges, RoofSide.END),
              extraFrontFace,
              polyLine2ByTwoPoints(leftRail[2], leftRail[3], rightRail[2], rightRail[3]),
              polyLine2ByTwoPoints(leftRail[2], leftRail[3], rightRail[2], rightRail[3]),
              RoofSide.END,
              true,
            ),
          );
        }
      }

      if (hasStartExtension && endFrontFace) {
        const extraFrontFace = faceOrNullCopy(endFrontFace);
        faceOrNullMoveAlongNormal(extraFrontFace, -polyLine2Length(leftRail));

        if (extraFrontFace) {
          vaultedRoofCapFaces.push(
            roofWallFacesFromFrontFace(
              getRoofSideWallFramingType(edges, RoofSide.START),
              extraFrontFace,
              polyLine2ByTwoPoints(leftRail[0], leftRail[1], rightRail[0], rightRail[1]),
              polyLine2ByTwoPoints(leftRail[0], leftRail[1], rightRail[0], rightRail[1]),
              RoofSide.START,
              true,
            ),
          );
        }
      }

      break;
    }
    default: {
      assertUnreachable(structureType);
    }
  }

  const clipBottomPolygons: PolygonBSP[] = [];

  if (bottomPerimeter) {
    const clipPerimeter = offsetPolyLine2(bottomPerimeter, -exteriorWallOffsetFromOverhangDatum);
    for (let i = 0, l = polyLine2EdgeCount(clipPerimeter); i < l; i++) {
      const [ax, ay] = polyLine2EdgeStart(clipPerimeter, i);
      const [bx, by] = polyLine2EdgeEnd(clipPerimeter, i);

      const edgePolygon = buildEdgePolygon(ax, ay, 0.0, bx, by, 0.0, 0.0, 0.0, 1.0);

      if (edgePolygon) {
        clipBottomPolygons.push(edgePolygon);
      }
    }
  }

  const clipBottomBSP = buildBSPNode({
    polygons: clipBottomPolygons,
    maxDepthErrorHandle: getMaxDepthErrorHandle(issues, { type: "ROOF", id }),
  });

  bottomFaces = clipFaces({
    faces: bottomFaces,
    options: {
      clippingNodes: [clippingNodeNew(clipBottomBSP)],
      epsilon: MULTIGABLE_BSP_EPSILON,
    },
  });

  return { bottomFaces, vaultedRoofCapFaces };
}
